import { useEffect, useRef } from 'react';
import { Icon } from '../lib/icons';
import { useAnalysisStore, useChatStore } from '../lib/stores';
import { analyzeDiscourseTopic, analyzeZohoTicket, getConversations, prepareRelease, analyzeCommits, generateNextSteps, generateZohoNextSteps, generateAzureNextSteps } from '../lib/api';
import { fmtNum } from '../lib/format';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Global floating panel that shows analysis task progress.
 * Mounts at the app root so it persists across page navigation.
 * Also contains the background runner that processes analysis tasks.
 */
export function AnalysisPanel() {
  const { tasks, panelOpen, setPanelOpen, addLog, updateTask, clearDone, appendStreamingContent, clearStreamingContent } = useAnalysisStore();
  const setSessions = useChatStore((s) => s.setSessions);
  const queryClient = useQueryClient();
  const logRef = useRef<HTMLDivElement>(null);
  const streamRef = useRef<HTMLDivElement>(null);
  const processingRef = useRef<Set<string>>(new Set());

  // Auto-scroll logs and stream
  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
    if (streamRef.current) {
      streamRef.current.scrollTop = streamRef.current.scrollHeight;
    }
  }, [tasks]);

  // Background runner: process any task that's 'running' and not yet being processed
  useEffect(() => {
    for (const task of tasks) {
      if (task.status === 'running' && !processingRef.current.has(task.id)) {
        processingRef.current.add(task.id);
        processTask(task.id);
      }
    }
  }, [tasks]);

  // Auto-dismiss the dock once everything is done — but only if nothing failed.
  // A failure stays pinned so it's not missed; the user clears it manually.
  useEffect(() => {
    if (tasks.length === 0) return;
    const anyRunning = tasks.some((t) => t.status === 'running');
    const anyFailed = tasks.some((t) => t.status === 'failed' || t.failed > 0);
    if (anyRunning || anyFailed) return;
    const t = setTimeout(() => clearDone(), 2500);
    return () => clearTimeout(t);
  }, [tasks, clearDone]);

  const processTask = async (taskId: string) => {
    const store = useAnalysisStore.getState();
    const task = store.tasks.find((t) => t.id === taskId);
    if (!task) return;

    if (task.type === 'release-preparation') {
      await processReleaseTask(taskId);
      return;
    }
    
    if (task.type === 'commit-analysis') {
      await processCommitAnalysisTask(taskId);
      return;
    }

    if (task.type === 'zoho-analysis') {
      await processZohoTask(taskId);
      return;
    }

    if (task.type === 'azure-next-steps') {
      await processAzureTask(taskId);
      return;
    }

    await processDiscourseTask(taskId);
  };

  // Azure DevOps has no analysis feature — this runner only computes the
  // next-steps recommendation for each selected work item (Feature/Story/Bug).
  const processAzureTask = async (taskId: string) => {
    const store = useAnalysisStore.getState();
    const task = store.tasks.find((t) => t.id === taskId);
    if (!task || !task.workItems) return;

    const items = task.workItems;
    const concurrency = task.provider === 'openai' ? Math.min(10, items.length) : 1;
    addLog(taskId, { type: 'info', text: `Computing next steps for ${fmtNum(items.length)} work item(s) using ${task.provider}/${task.model}${concurrency > 1 ? ` · ${fmtNum(concurrency)} parallel` : ''}` });

    let ok = 0;
    let fail = 0;
    let next = 0;
    const active = new Set<number>();
    const syncActive = () => updateTask(taskId, { activeIds: [...active], succeeded: ok, failed: fail, currentIndex: ok + fail });

    const worker = async () => {
      while (next < items.length) {
        const i = next++;
        const wi = items[i];
        active.add(wi.id);
        syncActive();
        addLog(taskId, { type: 'info', text: `[${fmtNum(i + 1)}/${fmtNum(items.length)}] #${wi.id} ${wi.title}` });
        try {
          const result = await generateAzureNextSteps(
            wi.id,
            (progressMsg) => { addLog(taskId, { type: 'info', text: `  ↳ ${progressMsg}` }); },
          );
          ok++;
          addLog(taskId, { type: 'success', text: `✓ #${wi.id}: ${result.primary?.label ?? 'next steps ready'}` });
          queryClient.invalidateQueries({ queryKey: ['azure-next-steps', wi.id] });
          queryClient.invalidateQueries({ queryKey: ['azure-work-item', String(wi.id)] });
        } catch (err: any) {
          fail++;
          addLog(taskId, { type: 'error', text: `✗ #${wi.id}: ${err.message || 'Unknown error'}` });
        } finally {
          active.delete(wi.id);
          syncActive();
        }
      }
    };

    await Promise.all(Array.from({ length: Math.max(1, concurrency) }, worker));

    addLog(taskId, { type: 'info', text: `Done. ${fmtNum(ok)} succeeded, ${fmtNum(fail)} failed.` });
    updateTask(taskId, { status: 'done', succeeded: ok, failed: fail, activeIds: [] });
    processingRef.current.delete(taskId);
    queryClient.invalidateQueries({ queryKey: ['azure-work-items'] });
  };

  const processZohoTask = async (taskId: string) => {
    const store = useAnalysisStore.getState();
    const task = store.tasks.find((t) => t.id === taskId);
    if (!task || !task.tickets) return;

    const tickets = task.tickets;
    const concurrency = task.provider === 'openai' ? Math.min(10, tickets.length) : 1;
    addLog(taskId, { type: 'info', text: `Starting analysis of ${fmtNum(tickets.length)} ticket(s) using ${task.provider}/${task.model}${concurrency > 1 ? ` · ${fmtNum(concurrency)} parallel` : ''}` });

    let ok = 0;
    let fail = 0;
    let next = 0;
    const active = new Set<string>();
    const syncActive = () => updateTask(taskId, { activeIds: [...active], succeeded: ok, failed: fail, currentIndex: ok + fail });

    const worker = async () => {
      while (next < tickets.length) {
        const i = next++;
        const ticket = tickets[i];
        active.add(ticket.id);
        syncActive();
        addLog(taskId, { type: 'info', text: `[${fmtNum(i + 1)}/${fmtNum(tickets.length)}] ${ticket.subject}` });
        try {
          const result = await analyzeZohoTicket(
            {
              ticketId: ticket.id,
              provider: task.provider!,
              model: task.model!,
              think: task.think ?? (task.provider === 'ollama' ? true : 'medium'),
              numCtx: task.provider === 'ollama' ? (task.numCtx ?? 8192) : null,
            },
            (progressMsg) => { addLog(taskId, { type: 'info', text: `  ↳ ${progressMsg}` }); },
          );
          ok++;
          addLog(taskId, { type: 'success', text: `✓ ${ticket.subject}: ${result.severity} — ${result.highlights}` });
          queryClient.invalidateQueries({ queryKey: ['zoho-analyses'] });
          queryClient.invalidateQueries({ queryKey: ['zoho-ticket-analyses', ticket.id] });

          // AI assist: also compute next-steps for this ticket.
          if (task.withNextSteps) {
            try {
              await generateZohoNextSteps(ticket.id);
              addLog(taskId, { type: 'success', text: `  ↳ ${ticket.subject}: next steps ready` });
              queryClient.invalidateQueries({ queryKey: ['zoho-next-steps', ticket.id] });
            } catch (e: any) {
              addLog(taskId, { type: 'error', text: `  ↳ ${ticket.subject}: next steps failed: ${e.message || 'error'}` });
            }
          }
        } catch (err: any) {
          fail++;
          addLog(taskId, { type: 'error', text: `✗ ${ticket.subject}: ${err.message || 'Unknown error'}` });
        } finally {
          active.delete(ticket.id);
          syncActive();
        }
      }
    };

    await Promise.all(Array.from({ length: Math.max(1, concurrency) }, worker));

    addLog(taskId, { type: 'info', text: `Done. ${fmtNum(ok)} succeeded, ${fmtNum(fail)} failed.` });
    updateTask(taskId, { status: 'done', succeeded: ok, failed: fail, activeIds: [] });
    processingRef.current.delete(taskId);

    queryClient.invalidateQueries({ queryKey: ['zoho-analyses'] });
    queryClient.invalidateQueries({ queryKey: ['zoho-ticket-analyses'] });

    try {
      const conversations = await getConversations();
      setSessions(conversations);
    } catch { /* ignore */ }
  };

  const processDiscourseTask = async (taskId: string) => {
    const store = useAnalysisStore.getState();
    const task = store.tasks.find((t) => t.id === taskId);
    if (!task || !task.topics) return;

    const topics = task.topics;
    // OpenAI tolerates parallel requests, so fan out up to 10 workers; Ollama is
    // GPU-bound locally, so stay sequential.
    const concurrency = task.provider === 'openai' ? Math.min(10, topics.length) : 1;
    addLog(taskId, { type: 'info', text: `Starting analysis of ${fmtNum(topics.length)} topic(s) using ${task.provider}/${task.model}${concurrency > 1 ? ` · ${fmtNum(concurrency)} parallel` : ''}` });

    let ok = 0;
    let fail = 0;
    let next = 0;
    const active = new Set<number>();
    const syncActive = () => updateTask(taskId, { activeIds: [...active], succeeded: ok, failed: fail, currentIndex: ok + fail });

    const worker = async () => {
      while (next < topics.length) {
        const i = next++;
        const topic = topics[i];
        active.add(topic.id);
        syncActive();
        addLog(taskId, { type: 'info', text: `[${fmtNum(i + 1)}/${fmtNum(topics.length)}] ${topic.title}` });
        try {
          const result = await analyzeDiscourseTopic(
            {
              topicId: topic.id,
              provider: task.provider!,
              model: task.model!,
              think: task.think ?? (task.provider === 'ollama' ? true : 'medium'),
              numCtx: task.provider === 'ollama' ? (task.numCtx ?? 8192) : null,
            },
            (progressMsg) => { addLog(taskId, { type: 'info', text: `  ↳ ${progressMsg}` }); },
          );
          ok++;
          addLog(taskId, { type: 'success', text: `✓ ${topic.title}: ${result.severity} — ${result.highlights}` });
          queryClient.invalidateQueries({ queryKey: ['discourse-analyses'] });
          queryClient.invalidateQueries({ queryKey: ['discourse-topic-analyses', topic.id] });

          // AI assist: also compute the next-steps recommendation for this topic.
          if (task.withNextSteps) {
            try {
              await generateNextSteps(topic.id);
              addLog(taskId, { type: 'success', text: `  ↳ ${topic.title}: next steps ready` });
              queryClient.invalidateQueries({ queryKey: ['discourse-next-steps', topic.id] });
            } catch (e: any) {
              addLog(taskId, { type: 'error', text: `  ↳ ${topic.title}: next steps failed: ${e.message || 'error'}` });
            }
          }
        } catch (err: any) {
          fail++;
          addLog(taskId, { type: 'error', text: `✗ ${topic.title}: ${err.message || 'Unknown error'}` });
        } finally {
          active.delete(topic.id);
          syncActive();
        }
      }
    };

    await Promise.all(Array.from({ length: Math.max(1, concurrency) }, worker));

    addLog(taskId, { type: 'info', text: `Done. ${fmtNum(ok)} succeeded, ${fmtNum(fail)} failed.` });
    updateTask(taskId, { status: 'done', succeeded: ok, failed: fail, activeIds: [] });
    processingRef.current.delete(taskId);

    // Refresh data
    queryClient.invalidateQueries({ queryKey: ['discourse-analyses'] });
    queryClient.invalidateQueries({ queryKey: ['discourse-topic-analyses'] });

    // Refresh sidebar conversations (updates Analysis Logs count)
    try {
      const conversations = await getConversations();
      setSessions(conversations);
    } catch { /* ignore */ }
  };

  const processReleaseTask = async (taskId: string) => {
    const task = useAnalysisStore.getState().tasks.find((t) => t.id === taskId);
    if (!task) return;

    addLog(taskId, { type: 'info', text: '🚀 Starting release preparation...' });

    try {
      // The backend INSERTs the release_sessions row (status 'preparing') before
      // emitting its first SSE event, so we can refresh the Prepare list as soon
      // as the first event arrives to surface the new "Preparing" row immediately.
      let listRefreshed = false;
      const { sessionId } = await prepareRelease(
        {
          name: task.name,
          scopeMode: task.scopeMode,
          periodStart: task.periodStart,
          periodEnd: task.periodEnd,
          selectedApps: task.selectedApps,
        },
        (event) => {
          if (!listRefreshed) {
            listRefreshed = true;
            queryClient.invalidateQueries({ queryKey: ['release-sessions'] });
          }
          switch (event.type) {
            case 'progress':
              addLog(taskId, { type: 'info', text: event.message || '' });
              break;
            case 'section_start':
              clearStreamingContent(taskId);
              addLog(taskId, { type: 'info', text: `\n📝 ${event.section}` });
              break;
            case 'section_done':
              addLog(taskId, { type: 'success', text: `  ✅ ${event.section} complete` });
              break;
            case 'app_start':
              clearStreamingContent(taskId);
              updateTask(taskId, { currentIndex: event.index || 0, totalItems: event.total });
              addLog(taskId, { type: 'info', text: `\n📦 Processing ${event.app} (${fmtNum((event.index || 0) + 1)}/${fmtNum(event.total ?? 0)})...` });
              break;
            case 'thinking':
              if (event.content) appendStreamingContent(taskId, event.content);
              break;
            case 'content':
              if (event.content) appendStreamingContent(taskId, event.content);
              break;
            case 'app_done':
              addLog(taskId, { type: 'success', text: `  ✅ ${event.app} report complete` });
              break;
            case 'error':
              addLog(taskId, { type: 'error', text: `  ❌ ${event.message}` });
              break;
            case 'done':
              break;
          }
        },
      );

      updateTask(taskId, { status: 'done', sessionId });
      addLog(taskId, { type: 'success', text: `\n✅ Release preparation complete!` });
      processingRef.current.delete(taskId);

      // Refresh release data
      queryClient.invalidateQueries({ queryKey: ['release-sessions'] });
      queryClient.invalidateQueries({ queryKey: ['release-status'] });
    } catch (err: any) {
      updateTask(taskId, { status: 'failed' });
      addLog(taskId, { type: 'error', text: `❌ Release preparation failed: ${err.message}` });
      processingRef.current.delete(taskId);
    }
  };

  const processCommitAnalysisTask = async (taskId: string) => {
    const store = useAnalysisStore.getState();
    const task = store.tasks.find((t) => t.id === taskId);
    if (!task || !task.commitIds) return;

    const commitIds = task.commitIds;
    // Each selected commit is analyzed on its own — one analysis per commit,
    // never bulked together. We loop sequentially so the stream stays readable.
    const cached = (queryClient.getQueryData(['commits']) as Array<{ id: string; repo: string; sha: string }> | undefined) || [];
    const labelFor = (id: string) => {
      const c = cached.find((x) => x.id === id);
      return c ? `${c.repo}@${c.sha.substring(0, 7)}` : id.substring(0, 8);
    };

    addLog(taskId, { type: 'info', text: `🔍 Analyzing ${fmtNum(commitIds.length)} commit(s) individually...` });

    let ok = 0;
    let fail = 0;
    let lastAnalysisId: string | undefined;

    for (let i = 0; i < commitIds.length; i++) {
      const commitId = commitIds[i];
      updateTask(taskId, { currentIndex: i });
      clearStreamingContent(taskId);
      addLog(taskId, { type: 'info', text: `\n📦 [${fmtNum(i + 1)}/${fmtNum(commitIds.length)}] ${labelFor(commitId)}` });

      try {
        const { analysisId } = await analyzeCommits(
          {
            commitIds: [commitId],
            provider: task.provider!,
            model: task.model!,
            think: task.think,
            numCtx: task.numCtx,
          },
          (event) => {
            switch (event.type) {
              case 'progress':
                addLog(taskId, { type: 'info', text: `  ↳ ${event.message || ''}` });
                break;
              case 'chunk':
                appendStreamingContent(taskId, event.text || '');
                break;
              case 'error':
                throw new Error(event.message);
              case 'done':
                break;
            }
          },
        );
        ok++;
        lastAnalysisId = analysisId;
        updateTask(taskId, { succeeded: ok });
        addLog(taskId, { type: 'success', text: `  ✅ ${labelFor(commitId)} done` });

        // Refresh this commit's views the moment its analysis lands, so updates
        // appear incrementally during the batch — not just at the end:
        //  - ['commits']            → the tracker list row (overall status badges)
        //  - ['commit', commitId]   → the detail-page header + its analyses history
        //    (prefix-matches ['commit', commitId, 'analyses']/'diff' too)
        queryClient.invalidateQueries({ queryKey: ['commits'] });
        queryClient.invalidateQueries({ queryKey: ['commit', commitId] });
      } catch (err: any) {
        fail++;
        updateTask(taskId, { failed: fail });
        addLog(taskId, { type: 'error', text: `  ❌ ${labelFor(commitId)} failed: ${err.message || 'Unknown error'}` });
      }
    }

    addLog(taskId, { type: 'info', text: `\nDone. ${fmtNum(ok)} succeeded, ${fmtNum(fail)} failed.` });
    updateTask(taskId, { status: 'done', succeeded: ok, failed: fail, analysisId: lastAnalysisId });
    processingRef.current.delete(taskId);

    queryClient.invalidateQueries({ queryKey: ['commits'] });
    queryClient.invalidateQueries({ queryKey: ['commit-analyses'] });
  };

  const runningTasks = tasks.filter((t) => t.status === 'running');
  const doneTasks = tasks.filter((t) => t.status !== 'running');
  const activeTask = runningTasks[0] || doneTasks[0];

  if (tasks.length === 0) return null;

  const LOG_COLORS: Record<string, string> = {
    info: '#b0bec5',
    success: 'var(--accent-success)',
    error: 'var(--accent-error)',
  };

  return (
    <div className="analysis-dock" style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
    }}>
      {/* Minimized bar (always visible when tasks exist) */}
      {!panelOpen && (
        <div
          onClick={() => setPanelOpen(true)}
          style={{
            background: 'var(--bg-elevated)',
            backdropFilter: 'blur(12px)',
            borderRadius: 10,
            padding: '10px 14px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            cursor: 'pointer',
            border: '1px solid var(--border-subtle)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            color: 'var(--text-primary)',
            fontSize: 13,
          }}
        >
          {runningTasks.length > 0 && (
            <span style={{ animation: 'pulse 1.5s infinite', display: 'inline-flex' }}><Icon name="zap" size={14} /></span>
          )}
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {runningTasks.length > 0
              ? (runningTasks[0].type === 'release-preparation'
                ? 'Preparing release...'
                : runningTasks[0].type === 'commit-analysis'
                ? 'Analyzing commits...'
                : `Analyzing ${runningTasks[0].topics?.[runningTasks[0].currentIndex]?.title || '...'}`)
              : `Task complete — ${fmtNum(doneTasks.length)} task(s)`}
          </span>
          <span style={{
            fontSize: 10,
            padding: '2px 6px',
            borderRadius: 99,
            background: runningTasks.length > 0 ? 'var(--accent-primary-soft)' : 'var(--accent-success-soft)',
            color: runningTasks.length > 0 ? 'var(--accent-primary)' : 'var(--accent-success)',
            fontWeight: 600,
          }}>
            {runningTasks.length > 0 ? 'running' : 'done'}
          </span>
        </div>
      )}

      {/* Expanded panel */}
      {panelOpen && activeTask && (
        <div style={{
          background: 'var(--bg-elevated)',
          backdropFilter: 'blur(16px)',
          borderRadius: 12,
          border: '1px solid var(--border-subtle)',
          boxShadow: '0 8px 40px rgba(0,0,0,0.4)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}>
          {/* Header */}
          <div style={{
            padding: '10px 14px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            borderBottom: '1px solid var(--border-subtle)',
          }}>
            {runningTasks.length > 0 && (
              <span style={{ animation: 'pulse 1.5s infinite', display: 'inline-flex' }}><Icon name="zap" size={14} /></span>
            )}
            <span style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, flex: 1 }}>
              {runningTasks.length > 0
                ? (runningTasks[0].type === 'release-preparation' ? 'Release Preparation' : 'Analysis Running')
                : 'Task Complete'}
            </span>
            {doneTasks.length > 0 && (
              <button
                onClick={clearDone}
                style={{
                  background: 'transparent', border: 'none', color: 'var(--text-tertiary)',
                  fontSize: 11, cursor: 'pointer', padding: '2px 6px',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--text-secondary)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-tertiary)'; }}
              >Clear</button>
            )}
            <button
              onClick={() => setPanelOpen(false)}
              style={{
                background: 'transparent', border: 'none', color: 'var(--text-tertiary)',
                fontSize: 16, cursor: 'pointer', padding: '0 4px', lineHeight: 1,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--text-secondary)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-tertiary)'; }}
            >−</button>
          </div>

          {/* Progress bar */}
          {activeTask.status === 'running' && (
            <div style={{ height: 3, background: 'var(--bg-secondary)' }}>
              <div style={{
                height: '100%',
                width: `${(() => {
                  const total = activeTask.totalItems || activeTask.topics?.length || activeTask.selectedApps?.length || activeTask.commitIds?.length || 0;
                  if (total === 0) return 0;
                  return ((activeTask.currentIndex + 1) / total) * 100;
                })()}%`,
                background: 'var(--accent-primary)',
                transition: 'width 0.3s ease',
              }} />
            </div>
          )}

          {/* Log area */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            height: (activeTask.type === 'release-preparation' || activeTask.type === 'commit-analysis') ? 400 : 240,
            overflow: 'hidden',
          }}>
            {/* Top 50%: Task Logs */}
            <div
              ref={logRef}
              style={{
                flex: 1,
                overflow: 'auto',
                padding: '8px 12px',
                fontFamily: "'SF Mono', 'Cascadia Code', 'Fira Code', monospace",
                fontSize: 11,
                lineHeight: 1.6,
                borderBottom: (activeTask.type === 'release-preparation' || activeTask.type === 'commit-analysis') ? '1px solid var(--border-subtle)' : 'none',
              }}
            >
              {activeTask.logs.map((entry, i) => (
                <div key={i}>
                  <span style={{ color: 'var(--text-tertiary)', marginRight: 6 }}>{entry.timestamp}</span>
                  <span style={{ color: LOG_COLORS[entry.type] || 'var(--text-secondary)' }}>{entry.text}</span>
                </div>
              ))}
              {activeTask.status === 'running' && (
                <div style={{ color: 'var(--accent-primary)' }}>
                  <span style={{ animation: 'pulse 1.5s infinite' }}>▍</span>
                </div>
              )}
            </div>

            {/* Bottom 50%: Streaming Content */}
            {(activeTask.type === 'release-preparation' || activeTask.type === 'commit-analysis') && (
              <div
                ref={streamRef}
                style={{
                  flex: 1,
                  overflow: 'auto',
                  padding: '8px 12px',
                  fontFamily: "'SF Mono', 'Cascadia Code', 'Fira Code', monospace",
                  fontSize: 11,
                  lineHeight: 1.6,
                  color: 'var(--text-secondary)',
                  whiteSpace: 'pre-wrap',
                  background: 'var(--bg-base)',
                }}
              >
                {activeTask.streamingContent || <span style={{ color: 'var(--text-tertiary)', fontStyle: 'italic' }}>Waiting for AI stream...</span>}
              </div>
            )}
          </div>

          {/* Task summary */}
          {tasks.length > 1 && (
            <div style={{
              padding: '6px 12px',
              borderTop: '1px solid var(--border-subtle)',
              fontSize: 11,
              color: 'var(--text-tertiary)',
            }}>
              {fmtNum(runningTasks.length)} running · {fmtNum(doneTasks.length)} completed
            </div>
          )}
        </div>
      )}
    </div>
  );
}
